<script setup lang="ts">
import type { MainNavChild, MainNavItem } from '@/types/all'
// ==========================================
// VUE 3 COMPOSITION API WITH <script setup>
// ==========================================
// This is the modern way to write Vue components in Vue 3.
// The <script setup> syntax is a compile-time transform that allows
// us to use Composition API with less boilerplate code.

// ==========================================
// STEP 1: DEFINE COMPONENT PROPS
// ==========================================
// defineProps is a compiler macro (special function recognized by Vue)
// It declares what props this component can accept from its parent
// NOTE: When using TypeScript, you could also define prop types like:
const props = defineProps<{
  navigationItems: Array<MainNavItem>
  isSidebarOpen?: boolean
}>()

// ==========================================
// STEP 2: DEFINE COMPONENT EVENTS
// ==========================================
// defineEmits is another compiler macro that declares what
// custom events this component can emit to its parent
const emit = defineEmits([
  // This special "update:propName" format enables two-way binding
  // with v-model or :prop.sync in the parent component
  'update:isSidebarOpen',

  // Custom event when a navigation item with children is toggled
  'toggle-item',

  // Custom event when a navigation item is clicked
  'nav-click',
])

// ==========================================
// STEP 3: DEFINE COMPONENT METHODS/FUNCTIONS
// ==========================================
// In Composition API, methods are just regular functions

/**
 * Toggles the sidebar visibility and emits an event to update
 * the isSidebarOpen prop in the parent component
 */
const toggleSidebar = () => {
  // Emit an event to the parent suggesting to update the isSidebarOpen prop
  // with the opposite of its current value (toggle behavior)
  emit('update:isSidebarOpen', !props.isSidebarOpen)

  // NOTE: We have to emit an event rather than modifying props directly
  // because in Vue, props are read-only (one-way data flow from parent to child)
}

/**
 * Handles navigation item clicks with different behavior based on
 * whether the item has children or not
 *
 * @param item - The navigation item that was clicked
 * @param event - The original click event
 */
const handleNavClick = (item: MainNavChild, event: Event) => {
  // Check if this navigation item has nested children
  if (item.children) {
    // Prevent the default link behavior (don't navigate)
    event.preventDefault()

    // Tell the parent to toggle this item's expanded state
    emit('toggle-item', item)
  } else {
    // For regular navigation items (no children)

    // On mobile devices, we want to close the sidebar after navigation
    if (window.innerWidth < 768) {
      emit('update:isSidebarOpen', false)
    }

    // Tell the parent that this navigation item was clicked
    // The parent can then handle actual navigation (e.g., router push)
    emit('nav-click', item)
  }
}

// ==========================================
// REACTIVITY NOTES
// ==========================================
// - Unlike Options API, there's no "data()" function here
// - For reactive local state, you would use:
//   import { ref, reactive } from 'vue'
//   const count = ref(0)  // For primitive values
//   const user = reactive({ name: 'John' })  // For objects

// - Any variables/functions declared here are automatically available in the template
// - No need to return anything (unlike in a regular setup() function)
</script>

<template>
  <aside
    class="flex flex-col w-48 h-screen fixed top-0 left-0 bg-gray-50 border-r border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out z-20"
    :class="{
      '-translate-x-full md:translate-x-0': !isSidebarOpen,
      'translate-x-0': isSidebarOpen,
    }"
  >
    <div class="h-14 shadow-md flex items-center justify-between p-4 border-b border-gray-200">
      <div class="font-bold text-xl">
        <slot name="logo">
          <!-- Default logo placeholder -->
          <div class="text-gray-500 text-xl">
            <span>Flywell</span>
          </div>
        </slot>
      </div>
      <button
        class="md:hidden bg-transparent border-none text-gray-500 cursor-pointer"
        @click="toggleSidebar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <nav class="flex-1 py-4">
      <div v-for="(section, sectionIndex) in navigationItems" :key="sectionIndex" class="mb-6">
        <div
          v-if="section.title"
          class="px-6 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider"
        >
          {{ section.title }}
        </div>
        <ul class="mt-2 mb-6">
          <li v-for="(item, itemIndex) in section.items" :key="itemIndex" class="my-1">
            <a
              :href="item.link"
              class="block px-6 py-2 text-gray-700 text-base hover:text-sky-500 hover:bg-gray-100 transition-colors border-l-2 border-transparent"
              :class="{ 'text-sky-500 border-l-sky-500 bg-gray-100 font-medium': item.active }"
              @click="handleNavClick(item, $event)"
            >
              {{ item.text }}
            </a>
            <!-- Nested items -->
            <ul v-if="item.children && item.expanded" class="m-0 p-0 list-none">
              <li v-for="(child, childIndex) in item.children" :key="childIndex" class="m-0">
                <a
                  :href="child.link"
                  class="block px-6 py-1.5 pl-11 text-gray-500 text-sm hover:text-sky-500 transition-colors"
                  :class="{ 'text-sky-500 font-medium': child.active }"
                  @click="handleNavClick(child, $event)"
                >
                  {{ child.text }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </nav>

    <div class="p-4 border-t border-gray-200 text-sm text-gray-500">
      <slot name="footer">
        <!-- Default footer content -->
        <div class="text-center">v1.0.0</div>
      </slot>
    </div>
  </aside>

  <!-- Mobile overlay -->
  <div
    v-if="isSidebarOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
    @click="toggleSidebar"
  ></div>
</template>
